<template>
  <div class="page-container">
    <el-card v-loading="loading">
      <div slot="header" class="page-header">
        <span class="page-title">产品详情</span>
        <div class="page-actions">
          <el-button size="small" icon="el-icon-arrow-left" @click="handleBack">返回</el-button>
          <el-button
            v-permission="['product:update']"
            type="primary"
            size="small"
            icon="el-icon-edit"
            @click="handleEdit"
          >编辑</el-button>
        </div>
      </div>

      <template v-if="product">
        <el-form label-width="130px" class="product-form" size="small">

          <el-form-item label="产品号">
            <span class="val-text">{{ product.product_no }}</span>
          </el-form-item>

          <el-form-item label="产品名称">
            <span class="val-text">{{ product.name }}</span>
          </el-form-item>

          <el-form-item label="产品略称">
            <span class="val-text">{{ product.short_name || '—' }}</span>
          </el-form-item>

          <!-- 产品大图 -->
          <el-form-item label="产品大图">
            <template v-if="product.images && product.images.length">
              <div class="image-list">
                <el-image
                  v-for="(url, idx) in product.images"
                  :key="idx"
                  :src="url"
                  :preview-src-list="product.images"
                  fit="cover"
                  class="image-box"
                />
              </div>
            </template>
            <span v-else class="val-empty">暂未上传</span>
          </el-form-item>

          <!-- 产品缩略图 -->
          <el-form-item label="产品缩略图">
            <el-image
              v-if="product.thumbnail"
              :src="product.thumbnail"
              :preview-src-list="[product.thumbnail]"
              fit="cover"
              class="image-box"
            />
            <span v-else class="val-empty">暂未上传</span>
          </el-form-item>

          <el-form-item label="卖点">
            <span class="val-text">{{ product.selling_points || '—' }}</span>
          </el-form-item>

          <el-form-item label="产品简介">
            <span v-if="product.intro" class="val-text intro-text">{{ product.intro }}</span>
            <span v-else class="val-empty">—</span>
          </el-form-item>

          <el-form-item label="产品详情">
            <template v-if="product.detail">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="rich-content" v-html="product.detail" />
            </template>
            <span v-else class="val-empty">—</span>
          </el-form-item>

          <el-form-item label="购买须知">
            <template v-if="product.purchase_instructions">
              <!-- eslint-disable-next-line vue/no-v-html -->
              <div class="rich-content" v-html="product.purchase_instructions" />
            </template>
            <span v-else class="val-empty">—</span>
          </el-form-item>

          <el-form-item label="划线价">
            <span v-if="product.original_price" class="val-price-origin">
              ¥{{ Number(product.original_price).toFixed(2) }}
            </span>
            <span v-else class="val-empty">—</span>
          </el-form-item>

          <el-form-item label="实收价">
            <span class="val-price">¥{{ Number(product.price).toFixed(2) }}</span>
          </el-form-item>

          <el-form-item label="显示顺序">
            <span class="val-text">{{ product.sort }}</span>
          </el-form-item>

          <el-form-item label="采样样本">
            <template v-if="product.sample_types && product.sample_types.length">
              <el-tag
                v-for="t in product.sample_types"
                :key="t"
                :type="t === 'blood' ? 'danger' : 'warning'"
                size="small"
                style="margin-right:6px"
              >{{ t === 'blood' ? '血液' : '组织' }}</el-tag>
            </template>
            <span v-else class="val-empty">—</span>
          </el-form-item>

          <el-form-item label="产品状态">
            <el-tag :type="product.status === 1 ? 'success' : 'info'" size="small">
              {{ product.status === 1 ? '上架' : '下架' }}
            </el-tag>
          </el-form-item>

          <el-form-item label="创建时间">
            <span class="val-text">{{ product.created_at }}</span>
          </el-form-item>

          <el-form-item label="最后更新">
            <span class="val-text">{{ product.updated_at }}</span>
          </el-form-item>

          <el-form-item>
            <el-button size="small" icon="el-icon-arrow-left" @click="handleBack">返回列表</el-button>
            <el-button
              v-permission="['product:update']"
              type="primary"
              size="small"
              icon="el-icon-edit"
              @click="handleEdit"
            >编辑产品</el-button>
          </el-form-item>
        </el-form>
      </template>
    </el-card>
  </div>
</template>

<script>
import { getProductDetail } from '@/api/product'

export default {
  name: 'ProductDetail',
  data() {
    return {
      loading: false,
      product: null
    }
  },
  computed: {
    productId() {
      return this.$route.query.id ? Number(this.$route.query.id) : null
    }
  },
  watch: {
    $route: {
      immediate: true,
      handler(to) {
        if (to.path !== '/product/detail') return
        if (to.query.id) {
          this.fetchDetail()
        } else {
          this.$message.error('缺少产品ID')
          this.$router.push('/product/index')
        }
      }
    }
  },
  methods: {
    async fetchDetail() {
      this.loading = true
      try {
        const res = await getProductDetail({ id: this.productId })
        this.product = res.data
      } finally {
        this.loading = false
      }
    },

    handleEdit() {
      this.$router.push(`/product/edit?id=${this.productId}`)
    },

    handleBack() {
      this.$router.push('/product/index')
    }
  }
}
</script>

<style lang="scss" scoped>
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .page-title {
    font-size: 15px;
    font-weight: 600;
    color: #303133;
  }

  .page-actions {
    display: flex;
    gap: 8px;
  }
}

.product-form {
  max-width: 860px;
  padding-top: 8px;
}

/* 值文本 */
.val-text {
  color: #303133;
  font-size: 13px;
  line-height: 1.6;
}

.val-empty {
  color: #c0c4cc;
  font-size: 13px;
}

.intro-text {
  display: block;
  white-space: pre-wrap;
  line-height: 1.7;
}

.val-price {
  color: #f56c6c;
  font-weight: 600;
  font-size: 15px;
}

.val-price-origin {
  color: #909399;
  text-decoration: line-through;
  font-size: 13px;
}

/* 图片列表 */
.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.image-box {
  width: 120px;
  height: 120px;
  border-radius: 4px;
  border: 1px solid #e4e7ed;
  display: block;
  cursor: pointer;
}

/* 富文本内容 */
.rich-content {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px 20px;
  background: #fafafa;
  max-width: 700px;
  line-height: 1.8;
  font-size: 14px;
  color: #303133;
  max-height: 500px;
  overflow: auto;

  ::v-deep h1, ::v-deep h2, ::v-deep h3 { margin: 12px 0 8px; font-weight: 600; }
  ::v-deep p  { margin: 0 0 10px; }
  ::v-deep ul, ::v-deep ol { padding-left: 20px; margin-bottom: 10px; }
  ::v-deep img { max-width: 100%; height: auto; border-radius: 4px; }
  ::v-deep a   { color: #409eff; }
  ::v-deep table { border-collapse: collapse; width: 100%; }
  ::v-deep td, ::v-deep th { border: 1px solid #e4e7ed; padding: 6px 10px; }
}
</style>
